import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, ArrowUpRight, Rocket, Download, FileText } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import ShareButtons from "@/components/site/ShareButtons";
import PledgeCard from "@/components/site/PledgeCard";
import { useAmazonLink } from "@/hooks/useAmazonLink";
import { ASSETS, PDFS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, setUser, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { link: amazonLink } = useAmazonLink();
  // null = checking, true = authed, false = not authed
  const [status, setStatus] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) {
      setUser(location.state.user);
      setStatus(true);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/auth/me`, { credentials: "include" });
        if (!res.ok) throw new Error("unauth");
        setUser(await res.json());
        setStatus(true);
      } catch {
        setStatus(false);
        navigate("/", { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (status === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-void">
        <div className="h-10 w-10 animate-spin border-2 border-rebellion border-t-transparent" />
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-void">
      {/* Member header */}
      <header className="fixed top-0 left-0 z-50 w-full border-b border-white/10 bg-void/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 sm:py-4 md:px-12">
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3" data-testid="dashboard-logo">
            <span className="h-2 w-2 shrink-0 bg-rebellion" />
            <span className="whitespace-nowrap font-mono text-[0.6rem] uppercase tracking-[0.2em] text-paper sm:text-xs sm:tracking-[0.35em]">
              election on mars
            </span>
          </Link>
          <div className="flex shrink-0 items-center gap-2 sm:gap-4">
            {user.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="h-7 w-7 shrink-0 border border-white/20 object-cover sm:h-8 sm:w-8"
                data-testid="user-avatar"
              />
            ) : null}
            <button
              data-testid="logout-button"
              onClick={handleLogout}
              className="flex shrink-0 items-center gap-1.5 whitespace-nowrap border border-white/20 px-3 py-2 font-mono text-[0.55rem] uppercase tracking-[0.15em] text-paper transition-colors hover:border-rebellion hover:text-rebellion sm:gap-2 sm:px-4 sm:py-2 sm:text-[0.65rem] sm:tracking-[0.2em]"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="vignette mx-auto max-w-7xl px-6 pb-24 pt-36 md:px-12 md:pt-44" data-testid="dashboard-page">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="mb-5 font-mono text-xs uppercase tracking-[0.3em] text-rebellion"
        >
          The Citizen&rsquo;s Vault — Member Access
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="font-display text-5xl uppercase leading-[0.9] tracking-tight text-paper sm:text-6xl md:text-7xl"
          data-testid="dashboard-welcome"
        >
          Welcome, <span className="text-rebellion">{user.name?.split(" ")[0] || "Citizen"}.</span>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-6 max-w-2xl font-editorial text-xl leading-relaxed text-white/70 md:text-2xl"
        >
          You&rsquo;re in. Below is a pledge worth sitting with, a sample to
          read, and a direct line to your copy of{" "}
          <em className="text-paper">Election on Mars</em>.
        </motion.p>

        <div className="mt-16 grid grid-cols-1 gap-10 lg:grid-cols-12">
          {/* Signable Pledge */}
          <div className="lg:col-span-7">
            <PledgeCard />
          </div>

          {/* Cover + CTA */}
          <div className="lg:col-span-5">
            <div className="relative mx-auto w-[70%] sm:w-[50%] lg:w-full">
              <div className="absolute -inset-4 -z-10 bg-rebellion/20 blur-2xl" />
              <div className="border border-white/15 shadow-[20px_20px_60px_rgba(0,0,0,0.8)]">
                <img src={ASSETS.cover} alt="Election on Mars book cover" className="block w-full" />
              </div>
            </div>
            <a
              data-testid="dashboard-buy-button"
              href={amazonLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-8 flex w-full items-center justify-center gap-3 border border-rebellion bg-rebellion px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-rebellion"
            >
              Buy on Amazon
              <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </div>

        {/* Free Reader Perk — PDF Downloads (member only) */}
        <section className="mt-24 border-t border-white/10 pt-16" data-testid="downloads-section">
          <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="mb-4 font-mono text-xs uppercase tracking-[0.3em] text-rebellion">
                Members Only — First Look
              </p>
              <h2 className="font-display text-3xl uppercase leading-[0.95] tracking-tight text-paper sm:text-4xl md:text-5xl">
                Download the <span className="text-rebellion">Sample.</span>
              </h2>
            </div>
            <p className="max-w-md font-editorial text-lg leading-relaxed text-white/60">
              A prologue and the first six chapters, on the house. Read before
              you buy — then buy so we can print more.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {Object.entries(PDFS).map(([key, pdf], i) => (
              <motion.a
                key={key}
                data-testid={`download-${key}`}
                href={pdf.url}
                target="_blank"
                rel="noopener noreferrer"
                download={pdf.filename}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
                whileHover={{ y: -4 }}
                className="group flex items-center justify-between gap-6 border border-white/10 bg-panel p-6 transition-colors duration-300 hover:border-rebellion md:p-8"
              >
                <div className="flex items-start gap-5">
                  <span className="mt-1 flex h-12 w-12 shrink-0 items-center justify-center border border-white/15 group-hover:border-rebellion group-hover:text-rebellion">
                    <FileText className="h-5 w-5" />
                  </span>
                  <div>
                    <span className="font-mono text-[0.6rem] uppercase tracking-[0.3em] text-white/40">
                      PDF · Free excerpt
                    </span>
                    <h3 className="mt-2 font-display text-2xl uppercase tracking-tight text-paper">
                      {pdf.label}
                    </h3>
                  </div>
                </div>
                <Download className="h-5 w-5 shrink-0 text-white/50 transition-colors group-hover:text-rebellion" />
              </motion.a>
            ))}
          </div>
        </section>

        {/* Spread the Manifesto */}
        <section className="mt-20" data-testid="dashboard-share-section">
          <div className="flex flex-wrap items-center gap-3">
            <Rocket className="h-4 w-4 text-rebellion" />
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-white/50">
              Spread the Word
            </span>
          </div>
          <p className="mb-6 mt-3 max-w-2xl font-editorial text-lg text-white/60">
            Send this to the next citizen who needs to read it.
          </p>
          <ShareButtons compact />
        </section>
      </main>
    </div>
  );
}
